import { clsx } from 'clsx';
import { Modal } from 'antd';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import React, { useState } from 'react';

import { CreditRisk } from '@types';
import { RiskTaxes } from '@pages/credit-risk/export/RiskTaxes';
import { RiskCrops } from '@pages/credit-risk/export/RiskCrops';
import { RiskVariables } from '@pages/credit-risk/export/RiskVariables';
import { BlueButton, InitialCard, Loader, VerqorLogo } from '@components';
import { GeneralInfo } from '@pages/credit-risk/RiskAnalysisReport/components/GeneralInfo';
import { CreditBureauTables } from '@pages/credit-risk/RiskAnalysisReport/components/CreditBureau';
import { FinancialRatios } from '@pages/credit-risk/RiskAnalysisReport/components/FinancialRatios';
import { Variables } from '@pages/credit-risk/RiskAnalysisReport/components/Variables';
import cls from './RiskAnalysisReport.module.css';


interface Props {
  risk: CreditRisk;
}

export const RiskAnalysisReport = ({ risk }: Props) => {
  const [isReportOpened, setReportOpened] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setExporting] = useState(false);
  const pdfFilename = `credit_analysis_${risk?.credit?.code}.pdf`;

  const openReport = () => setReportOpened(true);
  const closeReport = () => {
    setReportOpened(false);
    setCurrentPage(1);
  };

  const onDownload = async () => {
    setExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pages = document.querySelectorAll(`.${cls.page}`);

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        const canvas = await html2canvas(page, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = doc.internal.pageSize.getWidth();
        const imgProps = doc.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (i !== 0) {
          doc.addPage();
        }

        doc.addImage(
          imgData, 'PNG', 0, 0, pdfWidth, imgHeight, '', 'FAST',
        );
      }

      doc.save(pdfFilename);
    } finally {
      setExporting(false);
    }
  };

  const onChangePage = (isIncrement: boolean) => {
    isIncrement ? setCurrentPage(currentPage + 1) : setCurrentPage(currentPage - 1);
    const wrap = document.querySelector('.ant-modal-wrap');
    wrap && wrap.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <InitialCard autoHeight extraPadding>
        <div className={cls.box}>
          <div className={cls.title}>Credit Report</div>
          <BlueButton extraPadding className={cls.btn} onClick={openReport}>
            Open Report
          </BlueButton>
        </div>
      </InitialCard>

      <Modal
        open={isReportOpened}
        onCancel={closeReport}
        className={cls.modal}
        wrapClassName={cls.modalWrap}
        maskClosable={false}
        closable={!isExporting}
        width={700}
        title={(
          <div className={cls.modalHeader}>
            <div className={cls.modalHeaderAction}>
              <BlueButton className={cls.btn} onClick={onDownload} disabled={isExporting}>
                Download PDF
              </BlueButton>
            </div>
            <div className={cls.modalHeaderTitle}>
              File name: {pdfFilename}
            </div>
          </div>
        )}
        closeIcon={<div className={cls.close}/>}
        footer={(
          <div className={cls.modalFooter}>
            <button
              className={clsx(cls.btnPageAction, cls.btnPagePrev)}
              onClick={() => onChangePage(false)}
              disabled={currentPage === 1}
            />
            <div className={cls.modalPages}>PAGE {currentPage}/3</div>
            <button
              className={clsx(cls.btnPageAction, cls.btnPageNext)}
              onClick={() => onChangePage(true)}
              disabled={currentPage === 3}
            />
          </div>
        )}
      >
        <div className={cls.modalContent}>
          <div className={clsx(cls.file, isExporting && cls.pdfFile)}>
            {isExporting ? <Loader className={cls.loader}/> : null}
            {/* Page 1 */}
            <div className={clsx(
              cls.page, cls.page1, (currentPage === 1 || isExporting) && cls.active,
            )}>
              <div className={cls.logo}><VerqorLogo /></div>
              {/* General Info and Score */}
              <GeneralInfo risk={risk} />
              {/* Income Verification */}
              <RiskVariables risk={risk} noTitle />
              <div className={cls.pages}>
                <span className={cls.pagesNum}>1</span>
              </div>
            </div>
            {/* Page 2 */}
            <div className={clsx(
              cls.page, cls.page2, (currentPage === 2 || isExporting) && cls.active,
            )}>
              <div className={cls.logo}><VerqorLogo /></div>
              {/* Credit Bureau */}
              <CreditBureauTables risk={risk} />
              {/* Summary of Fiscal Income & Expenses */}
              <RiskTaxes
                risk={risk}
                twoColors
                title="Income and Expenses"
                className={cls.exportTable}
              />
              {/* Fiscal Income of Crop to Finance */}
              <RiskCrops
                risk={risk}
                twoColors
                title="Income and Expenses (of Financed Crop)"
                className={cls.exportTable}
              />
              {/* Financial Ratios */}
              <FinancialRatios risk={risk}/>
              <div className={cls.pages}>
                <span className={cls.pagesNum}>2</span>
              </div>
            </div>
            {/* Page 3 */}
            <div className={clsx(
              cls.page, cls.page3, (currentPage === 3 || isExporting) && cls.active,
            )}>
              <div className={cls.logo}><VerqorLogo /></div>
              {/* Risk Variables */}
              <Variables risk={risk}/>
              <div className={cls.pages}>
                <span className={cls.pagesNum}>3</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
